import { useState } from 'react';
import { trpc } from '@/lib/trpc';
import { useAuth } from '@/lib/auth';

interface Props {
  courseId:   number;
  /** True if the current viewer has purchased this course — only then can they comment. */
  canComment: boolean;
}

/**
 * Public course comments / reviews.
 * Anyone can read; only buyers can post (server enforces).
 */
export default function CourseComments({ courseId, canComment }: Props) {
  const { user, isAdmin } = useAuth();
  const utils = trpc.useUtils();
  const { data: comments = [], isLoading } =
    trpc.comments.listByCourse.useQuery({ courseId });

  const [body, setBody] = useState('');
  const [rating, setRating] = useState<number>(5);

  const create = trpc.comments.create.useMutation({
    onSuccess: () => {
      setBody('');
      utils.comments.listByCourse.invalidate({ courseId });
    },
  });

  const del = trpc.comments.delete.useMutation({
    onSuccess: () => utils.comments.listByCourse.invalidate({ courseId }),
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!body.trim()) return;
    create.mutate({ courseId, body: body.trim(), rating });
  };

  return (
    <section className="mt-10 bg-[#0A0A0B] border border-[#27272A] rounded-3xl p-6">
      <div className="flex items-baseline justify-between mb-4">
        <h3 className="text-lg font-bold text-white">Reviews</h3>
        <span className="text-[#71717A] text-xs">{comments.length}</span>
      </div>

      {canComment ? (
        <form onSubmit={submit} className="mb-6 space-y-3">
          <div className="flex items-center gap-2 text-yellow-400 text-lg">
            {[1, 2, 3, 4, 5].map(n => (
              <button
                key={n}
                type="button"
                onClick={() => setRating(n)}
                className={n <= rating ? '' : 'opacity-30'}
                aria-label={`${n} star${n === 1 ? '' : 's'}`}
              >★</button>
            ))}
          </div>
          <textarea
            value={body}
            onChange={e => setBody(e.target.value)}
            placeholder="Share your experience with this course…"
            rows={3}
            maxLength={2000}
            className="w-full bg-black border border-[#27272A] rounded-2xl px-4 py-3
                       text-sm text-white placeholder-[#52525B] outline-none
                       focus:border-yellow-400/40 resize-none"
          />
          {create.error && (
            <p className="text-red-400 text-xs">{create.error.message}</p>
          )}
          <button
            type="submit"
            disabled={create.isPending || !body.trim()}
            className="mp-btn mp-btn-primary px-5 py-2 text-sm rounded-xl"
          >
            {create.isPending ? 'Posting…' : 'Post review'}
          </button>
        </form>
      ) : (
        <p className="text-[#71717A] text-sm mb-6">
          {user
            ? 'Purchase this course to leave a review.'
            : 'Sign in and buy this course to leave a review.'}
        </p>
      )}

      {isLoading ? (
        <p className="text-[#52525B] text-sm">Loading reviews…</p>
      ) : comments.length === 0 ? (
        <p className="text-[#52525B] text-sm">No reviews yet — be the first.</p>
      ) : (
        <ul className="space-y-4">
          {comments.map(c => (
            <li key={c.id} className="bg-black border border-[#27272A] rounded-2xl p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-yellow-400 text-black
                                  flex items-center justify-center text-xs font-bold">
                    {(c.author.name?.[0] ?? '?').toUpperCase()}
                  </div>
                  <span className="text-[#E4E4E7] text-sm font-semibold">{c.author.name}</span>
                  {c.rating != null && (
                    <span className="text-yellow-400 text-xs ml-1">
                      {'★'.repeat(c.rating)}<span className="opacity-30">{'★'.repeat(5 - c.rating)}</span>
                    </span>
                  )}
                </div>
                <span className="text-[#52525B] text-xs">
                  {new Date(c.createdAt).toLocaleDateString()}
                </span>
              </div>
              <p className="text-[#E4E4E7] text-sm whitespace-pre-wrap leading-relaxed">{c.body}</p>

              {(c.author.id === user?.id || isAdmin) && (
                <button
                  onClick={() => del.mutate({ id: c.id })}
                  disabled={del.isPending}
                  className="mt-2 text-[#52525B] hover:text-red-400 text-xs transition-colors"
                >
                  Delete
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
